import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormHelperText,
  InputLabel,
  Skeleton,
  Typography,
} from "@mui/material";
import CustomInput from "global/components/CustomInput/CustomInput";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import {
  getExpiryPassword,
  updateExpiryPassword,
} from "screens/Configurations/ConfigurationsService";
import {
  buttonRowSx,
  buttonSpinnerSx,
  cardDescriptionSx,
  cardHeaderSx,
  cardHeaderTextSx,
  cardSx,
  cardTitleSx,
  expiryFieldSx,
  expiryLabelSx,
  expiryRowSx,
  expiryRowStackSx,
  expirySuffixSx,
  fieldErrorSx,
  primaryButtonSx,
  skeletonHeaderTextSx,
  toggleKnobSx,
  toggleTrackSx,
  toggleWrapperSx,
} from "screens/Settings/SecurityPolicies/SecurityPolicies.styles";
import securityPoliciesTokens from "screens/Settings/SecurityPolicies/SecurityPolicies.tokens";

const { primaryButton, skeleton, toggle, expiryField } = securityPoliciesTokens;

const MAX_EXPIRY_DAYS = 365;
const EXPIRY_ERROR = "Please enter at least 1 day or more.";

interface ExpiryPolicy {
  pwdExpiryEnable: boolean;
  expirationPassword: number | string;
}

const INITIAL_POLICY: ExpiryPolicy = {
  pwdExpiryEnable: false,
  expirationPassword: 0,
};

/**
 * "Password Expiry" card — a headline toggle over an inline
 * "Password will expire in [ n ] days" row, closed by the Update action.
 * Everything visual comes from `SecurityPolicies.styles`.
 */
const PasswordExpiryCard = () => {
  const [policy, setPolicy] = useState<ExpiryPolicy>(INITIAL_POLICY);
  const [previousPolicy, setPreviousPolicy] =
    useState<ExpiryPolicy>(INITIAL_POLICY);
  const [expiryError, setExpiryError] = useState<string>("");
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchPolicy = useCallback(async () => {
    try {
      setFetchLoading(true);
      const response = await getExpiryPassword();
      const next: ExpiryPolicy = {
        pwdExpiryEnable: !!response?.pwdExpiryEnable,
        expirationPassword: response?.pwdExpiryRemainingDays ?? 0,
      };
      setPolicy(next);
      setPreviousPolicy(next);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const handleToggle = () => {
    setPolicy((prev) => ({
      ...prev,
      pwdExpiryEnable: !prev.pwdExpiryEnable,
    }));
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, "");
    if (numericValue && parseInt(numericValue, 10) > MAX_EXPIRY_DAYS) return;
    setPolicy((prev) => ({ ...prev, expirationPassword: numericValue }));
    setExpiryError(Number(numericValue) > 0 ? "" : EXPIRY_ERROR);
  };

  const isDirty =
    policy.pwdExpiryEnable !== previousPolicy.pwdExpiryEnable ||
    String(policy.expirationPassword) !==
      String(previousPolicy.expirationPassword);

  const isExpiryInvalid =
    policy.pwdExpiryEnable &&
    (Number(policy.expirationPassword) <= 0 ||
      Number(policy.expirationPassword) > MAX_EXPIRY_DAYS);

  const handleUpdate = async () => {
    if (isExpiryInvalid) {
      setExpiryError(EXPIRY_ERROR);
      return;
    }
    try {
      setSaving(true);
      await updateExpiryPassword(
        policy.pwdExpiryEnable,
        Number(policy.expirationPassword),
      );
      openSuccessNotification("Password expiry has been updated");
      setPreviousPolicy(policy);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSaving(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={cardSx}>
        <Box sx={cardHeaderSx}>
          <Box sx={skeletonHeaderTextSx}>
            <Skeleton
              variant="text"
              width={skeleton.titleWidth}
              height={skeleton.titleHeight}
            />
            <Skeleton
              variant="text"
              width={skeleton.descriptionWidth}
              height={skeleton.descriptionHeight}
            />
          </Box>
          <Skeleton
            variant="rounded"
            width={toggle.width}
            height={toggle.height}
            sx={{ ...toggleWrapperSx, borderRadius: "999px" }}
          />
        </Box>
        <Box sx={expiryRowSx}>
          <Skeleton
            variant="text"
            width={skeleton.rowLabelWidth}
            height={skeleton.rowLabelHeight}
          />
          <Skeleton
            variant="rounded"
            width={expiryField.width}
            height={expiryField.height}
            sx={{ borderRadius: expiryField.radius }}
          />
        </Box>
        <Skeleton
          variant="rounded"
          width={skeleton.buttonWidth}
          height={primaryButton.height}
          sx={{ borderRadius: primaryButton.radius }}
        />
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      <Box sx={cardHeaderSx}>
        <Box sx={cardHeaderTextSx}>
          <Typography component="h2" sx={cardTitleSx}>
            Password Expiry
          </Typography>
          <Typography component="div" sx={cardDescriptionSx()}>
            Everyone in the organization must set a new password after this
            period — anywhere from 1 to {MAX_EXPIRY_DAYS} days.
          </Typography>
        </Box>
        <Box component="span" sx={toggleWrapperSx}>
          <ButtonBase
            disableRipple
            role="switch"
            aria-checked={policy.pwdExpiryEnable}
            aria-label="Password Expiry"
            onClick={handleToggle}
            sx={toggleTrackSx(policy.pwdExpiryEnable)}
          >
            <Box sx={toggleKnobSx(policy.pwdExpiryEnable)} />
          </ButtonBase>
        </Box>
      </Box>

      <Box sx={expiryRowStackSx(policy.pwdExpiryEnable)}>
        <Box sx={expiryRowSx}>
          <InputLabel htmlFor="expirationPassword" sx={expiryLabelSx}>
            Password will expire in
          </InputLabel>
          <CustomInput
            required
            widthNotRequired
            id="expirationPassword"
            name="expirationPassword"
            type="text"
            textAlign="center"
            disabled={!policy.pwdExpiryEnable}
            onChange={handleOnChange}
            value={policy.expirationPassword}
            propsToInputElement={{
              minLength: 1,
              maxLength: 3,
              "aria-invalid": isExpiryInvalid && !!expiryError,
              "aria-describedby":
                isExpiryInvalid && expiryError
                  ? "expirationPassword-error"
                  : undefined,
            }}
            sx={expiryFieldSx(isExpiryInvalid && !!expiryError)}
          />
          <Typography component="span" sx={expirySuffixSx}>
            {Number(policy.expirationPassword) === 1 ? "day" : "days"}
          </Typography>
        </Box>
        {isExpiryInvalid && expiryError && (
          <FormHelperText
            error
            id="expirationPassword-error"
            role="alert"
            sx={fieldErrorSx}
          >
            {expiryError}
          </FormHelperText>
        )}
      </Box>

      <Box sx={buttonRowSx(policy.pwdExpiryEnable)}>
        <Button
          id="security_policies_expiry_update_btn"
          variant="contained"
          disabled={!isDirty || isExpiryInvalid || saving}
          onClick={handleUpdate}
          sx={primaryButtonSx}
        >
          {saving && (
            <CircularProgress
              size={primaryButton.spinnerSize}
              thickness={primaryButton.spinnerThickness}
              sx={buttonSpinnerSx}
            />
          )}
          Update
        </Button>
      </Box>
    </Box>
  );
};

export default PasswordExpiryCard;
